import { type LlmBlockDetail, getFileLlmBlocks } from "@/services/api";
import { llmReviewMessages } from "@/services/messages";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { BlockDetailExpand } from "./BlockDetailExpand";
import { BlocksTable } from "./BlocksTable";
import { SummaryCards } from "./SummaryCards";

interface LlmReviewSectionProps {
	fileId: number;
}

/**
 * Фича 035 — секция «LLM-ревью разбора» в детальной карточке файла-акта.
 *
 * Поведение:
 *   - useQuery с polling 2с пока counts.pending > 0; стабильно — выключаем.
 *   - Если все блоки `none` (LLM не вызывался ни на один) — показываем
 *     ненавязчивый empty_state, без таблицы.
 *   - Иначе — Summary (карточки-счётчики) + BlocksTable.
 *   - Клик по строке с hasReviewJson → раскрытие BlockDetailExpand (lazy fetch).
 */
export function LlmReviewSection({ fileId }: LlmReviewSectionProps): React.JSX.Element | null {
	const m = llmReviewMessages;

	const { data, isLoading, isError } = useQuery({
		queryKey: ["file-llm-blocks", fileId],
		queryFn: () => getFileLlmBlocks(fileId),
		refetchInterval: (q) => {
			const d = q.state.data;
			if (!d) return 2_000;
			return d.counts.pending > 0 ? 2_000 : false;
		},
		staleTime: 0,
		retry: 2,
	});

	const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

	if (isLoading) {
		return (
			<section className="rounded-xl border border-slate-200 bg-white p-4">
				<div className="flex items-center gap-2 text-sm text-slate-500">
					<Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
					<span>{m.section_title}…</span>
				</div>
			</section>
		);
	}

	if (isError || !data) {
		// Молча скрываем при ошибке (это вспомогательная секция, не блокирующая UX).
		return null;
	}

	const { blocks, counts } = data;
	if (counts.total === 0) return null;

	// Если у файла НЕТ ни одного блока, на который LLM реально что-то делал —
	// показываем ненавязчивый empty_state без раздутия UI.
	const hasAnyLlmActivity = counts.applied + counts.skipped + counts.failed + counts.pending > 0;

	const toggle = (blockId: number) => {
		setExpandedIds((prev) => {
			const next = new Set(prev);
			if (next.has(blockId)) next.delete(blockId);
			else next.add(blockId);
			return next;
		});
	};

	const renderExpand = (block: LlmBlockDetail) => (
		<BlockDetailExpand fileId={fileId} block={block} />
	);

	return (
		<section className="rounded-xl border border-slate-200 bg-white p-4">
			<header className="mb-3">
				<h2 className="text-base font-semibold text-slate-900">{m.section_title}</h2>
				<p className="mt-1 text-xs text-slate-500">{m.section_subtitle}</p>
			</header>

			{!hasAnyLlmActivity ? (
				<p className="rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
					{m.empty_state}
				</p>
			) : (
				<div className="space-y-4">
					<SummaryCards counts={counts} />
					<BlocksTable
						blocks={blocks}
						expandedIds={expandedIds}
						onToggle={toggle}
						renderExpand={renderExpand}
					/>
				</div>
			)}
		</section>
	);
}
